import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  GitBranch,
  Cloud,
  Workflow,
  Database,
  ShieldCheck,
  BadgeCheck,
  Plug,
  Siren,
  Wrench,
  DollarSign,
  TrendingUp,
  Wallet,
  Receipt,
} from "lucide-react";

type Section = {
  icon: React.ElementType;
  titleKey: keyof ReturnType<typeof useTranslation>["t"] extends never ? string : string;
  descKey: string;
  items: string[];
  badge: "required" | "recommended";
};

const TechnicalDocumentation = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const sections: Array<{
    icon: React.ElementType;
    title: string;
    desc: string;
    items: string[];
    badge: "required" | "recommended";
  }> = [
    {
      icon: Building2,
      title: t("techDocS1Title"),
      desc: t("techDocS1Desc"),
      items: [t("techDocS1I1"), t("techDocS1I2"), t("techDocS1I3"), t("techDocS1I4")],
      badge: "required",
    },
    {
      icon: GitBranch,
      title: t("techDocS2Title"),
      desc: t("techDocS2Desc"),
      items: [t("techDocS2I1"), t("techDocS2I2"), t("techDocS2I3"), t("techDocS2I4")],
      badge: "required",
    },
    {
      icon: Cloud,
      title: t("techDocS3Title"),
      desc: t("techDocS3Desc"),
      items: [t("techDocS3I1"), t("techDocS3I2"), t("techDocS3I3"), t("techDocS3I4")],
      badge: "required",
    },
    {
      icon: Workflow,
      title: t("techDocS4Title"),
      desc: t("techDocS4Desc"),
      items: [t("techDocS4I1"), t("techDocS4I2"), t("techDocS4I3"), t("techDocS4I4")],
      badge: "required",
    },
    {
      icon: Database,
      title: t("techDocS5Title"),
      desc: t("techDocS5Desc"),
      items: [t("techDocS5I1"), t("techDocS5I2"), t("techDocS5I3"), t("techDocS5I4")],
      badge: "required",
    },
    {
      icon: ShieldCheck,
      title: t("techDocS6Title"),
      desc: t("techDocS6Desc"),
      items: [t("techDocS6I1"), t("techDocS6I2"), t("techDocS6I3"), t("techDocS6I4")],
      badge: "required",
    },
    {
      icon: BadgeCheck,
      title: t("techDocS7Title"),
      desc: t("techDocS7Desc"),
      items: [t("techDocS7I1"), t("techDocS7I2"), t("techDocS7I3"), t("techDocS7I4")],
      badge: "required",
    },
    {
      icon: Plug,
      title: t("techDocS8Title"),
      desc: t("techDocS8Desc"),
      items: [t("techDocS8I1"), t("techDocS8I2"), t("techDocS8I3"), t("techDocS8I4")],
      badge: "recommended",
    },
    {
      icon: Siren,
      title: t("techDocS9Title"),
      desc: t("techDocS9Desc"),
      items: [t("techDocS9I1"), t("techDocS9I2"), t("techDocS9I3"), t("techDocS9I4")],
      badge: "required",
    },
    {
      icon: Wrench,
      title: t("techDocS10Title"),
      desc: t("techDocS10Desc"),
      items: [t("techDocS10I1"), t("techDocS10I2"), t("techDocS10I3"), t("techDocS10I4")],
      badge: "recommended",
    },
    {
      icon: DollarSign,
      title: t("techDocS11Title"),
      desc: t("techDocS11Desc"),
      items: [t("techDocS11I1"), t("techDocS11I2"), t("techDocS11I3"), t("techDocS11I4")],
      badge: "required",
    },
    {
      icon: TrendingUp,
      title: t("techDocS12Title"),
      desc: t("techDocS12Desc"),
      items: [t("techDocS12I1"), t("techDocS12I2"), t("techDocS12I3"), t("techDocS12I4")],
      badge: "required",
    },
    {
      icon: Wallet,
      title: t("techDocS13Title"),
      desc: t("techDocS13Desc"),
      items: [t("techDocS13I1"), t("techDocS13I2"), t("techDocS13I3"), t("techDocS13I4")],
      badge: "required",
    },
    {
      icon: Receipt,
      title: t("techDocS14Title"),
      desc: t("techDocS14Desc"),
      items: [t("techDocS14I1"), t("techDocS14I2"), t("techDocS14I3"), t("techDocS14I4")],
      badge: "required",
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">{t("techDocTitle")}</h1>
          <p className="text-muted-foreground mt-1">{t("techDocSubtitle")}</p>
        </div>

        {/* Intro banner */}
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground">
          {t("techDocIntro")}
        </div>

        {/* Legend */}
        <div className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-start gap-2">
            <Badge variant="destructive">{t("techDocRequired")}</Badge>
            <span className="text-muted-foreground">–</span>
            <span className="text-muted-foreground">Critical documents that must be provided</span>
          </div>
          <div className="flex items-start gap-2">
            <Badge variant="secondary">{t("techDocRecommended")}</Badge>
            <span className="text-muted-foreground">–</span>
            <span className="text-muted-foreground">Strongly advisable to request</span>
          </div>
        </div>

        {/* Sections grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sections.map((section) => (
            <Card key={section.title} className="flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <section.icon size={18} />
                    </div>
                    <CardTitle className="text-base leading-tight">{section.title}</CardTitle>
                  </div>
                  <Badge
                    variant={section.badge === "required" ? "destructive" : "secondary"}
                    className="self-start text-xs sm:shrink-0"
                  >
                    {section.badge === "required" ? t("techDocRequired") : t("techDocRecommended")}
                  </Badge>
                </div>
                <CardDescription className="mt-2 text-sm leading-relaxed pl-12">
                  {section.desc}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-2 pl-12">
                  {section.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TechnicalDocumentation;
